import { useState, useEffect, useRef, useCallback } from 'react';

interface UseAudioLevelOptions {
  smoothingFactor?: number;
  minDecibels?: number;
  maxDecibels?: number;
  fftSize?: number;
}

interface UseAudioLevelResult {
  audioLevel: number; // 0-100
  isActive: boolean;
  error: string | null;
  startAnalyzing: (stream: MediaStream) => void;
  stopAnalyzing: () => void;
}

const useAudioLevel = (options: UseAudioLevelOptions = {}): UseAudioLevelResult => {
  const {
    smoothingFactor = 0.8,
    minDecibels = -90,
    maxDecibels = -10,
    fftSize = 256,
  } = options;

  const [audioLevel, setAudioLevel] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const analyze = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average level
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    
    // Normalize to 0-100
    const normalizedLevel = Math.min(100, Math.max(0, (average / 255) * 100 * 1.5));
    
    setAudioLevel(normalizedLevel);
    animationFrameRef.current = requestAnimationFrame(analyze);
  }, []);

  const startAnalyzing = useCallback((stream: MediaStream) => {
    try {
      setError(null);
      
      // Create audio context
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        setError('AudioContext not supported');
        return;
      }

      audioContextRef.current = new AudioContextClass();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.smoothingTimeConstant = smoothingFactor;
      analyserRef.current.minDecibels = minDecibels;
      analyserRef.current.maxDecibels = maxDecibels;
      analyserRef.current.fftSize = fftSize;

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      setIsActive(true);
      analyze();
    } catch (err) {
      console.error('Error starting audio analysis:', err);
      setError('Failed to analyze audio');
    }
  }, [analyze, smoothingFactor, minDecibels, maxDecibels, fftSize]);

  const stopAnalyzing = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsActive(false);
    setAudioLevel(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAnalyzing();
    };
  }, [stopAnalyzing]);

  return {
    audioLevel,
    isActive,
    error,
    startAnalyzing,
    stopAnalyzing,
  };
};

export default useAudioLevel;
